import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { GitCompare, TrendingUp, Users, DollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FeatureGate } from "@/components/feature-gate";
import { getProgramComparison } from "@/lib/command-center.functions";

export const Route = createFileRoute("/_authenticated/intelligence/program-comparison")({
  component: Page,
});

function Page() {
  return (
    <FeatureGate feature="program_comparison" showBlurred={false}>
      <ProgramComparisonInner />
    </FeatureGate>
  );
}

function fmt(n: number | null | undefined) {
  if (n == null) return ",";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ProgramComparisonInner({ showHeader = true }: { showHeader?: boolean } = {}) {
  const fetchCompare = useServerFn(getProgramComparison);
  const now = new Date();
  const from = new Date(now); from.setDate(now.getDate() - 90);
  const { data, isLoading } = useQuery({
    queryKey: ["program-comparison"],
    queryFn: () => fetchCompare({ data: { from: from.toISOString(), to: now.toISOString() } }),
  });

  const programs = data?.programs ?? [];

  const ranked = useMemo(() => {
    return [...programs].sort((a: any, b: any) => (b.metrics?.total_revenue ?? 0) - (a.metrics?.total_revenue ?? 0));
  }, [programs]);

  const totalRevenue = ranked.reduce((s: number, p: any) => s + (p.metrics?.total_revenue ?? 0), 0);

  return (
    <div className="space-y-6">
      {showHeader && (
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <GitCompare className="h-7 w-7 text-primary" /> Program Comparison
          </h1>
          <p className="text-muted-foreground mt-1">
            See which programs actually drive your business, side by side.
          </p>
        </div>
      )}

      {isLoading && <Card><CardContent className="py-12 text-center text-muted-foreground">Loading...</CardContent></Card>}

      {!isLoading && ranked.length === 0 && (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          No programs to compare yet. Create workspaces and import data.
        </CardContent></Card>
      )}

      {ranked.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {ranked.map((p: any, idx: number) => {
            const m = p.metrics ?? {};
            const share = totalRevenue > 0 ? ((m.total_revenue ?? 0) / totalRevenue) * 100 : 0;
            return (
              <Card key={p.workspace_id} className={idx === 0 ? "border-primary/50 shadow-md" : ""}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base truncate">{p.program_name}</CardTitle>
                    {idx === 0 && <Badge className="bg-primary/15 text-primary border-primary/30 text-xs">Top</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground capitalize">{p.revenue_model?.replace("_", " ")}</div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Metric icon={DollarSign} label="Revenue (90d)" value={fmt(m.total_revenue)} sub={`${share.toFixed(1)}% of total`} />
                  <Metric icon={TrendingUp} label="MRR" value={fmt(m.mrr)} />
                  <Metric icon={Users} label="Active customers" value={(m.active_total ?? m.active_monthly ?? 0).toLocaleString()} />
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                    <Mini label="LTV" value={fmt(m.ltv)} />
                    <Mini label="Churn" value={m.churn_rate != null ? `${(m.churn_rate * 100).toFixed(1)}%` : ","} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub }: any) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="h-7 w-7 rounded-md bg-muted flex items-center justify-center shrink-0">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="font-semibold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}
